import React, { useState, useEffect } from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import ReferenceReservationStatistics from "./ReferenceReservationStatistics";
import "./index.css";

const ReservationHistoryTable = () => {
  const { reservationHistory, loading } = useSelector(
    (state) => state.reservationHistoryReducer
  );
  const [filteredData, setFilteredData] = useState(reservationHistory);

  useEffect(() => {
    setFilteredData(reservationHistory);
  }, [reservationHistory]);

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      align: "center",
      key: "date",
      width: "10%",
      render: (text, record) => (
        <div>{dayjs(record.date).format("ddd D.MM")}</div>
      ),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(), // unix transform to timestamp
      sortDirections: ["ascend", "descend"], // Ascending and descending sorting
    },
    {
      title: "Регион",
      dataIndex: "regionName",
      align: "center",
      key: "regionName",
      width: "10%",
      filterSearch: true,
      filters: [
        ...Array.from(
          new Set(reservationHistory.map((item) => item.regionName))
        ).map((region) => ({ text: region, value: region })),
      ],
      onFilter: (value, record) => record.regionName === value,
      sorter: (a, b) => a.regionName.localeCompare(b.regionName),
    },
    {
      title: "За",
      dataIndex: "startTime",
      align: "center",
      key: "startTime",
      width: "10%",
      render: (text, record) => (
        <div>{`${record.startTime.substring(0, 5)} - ${record.endTime.substring(
          0,
          5
        )}`}</div>
      ),
    },
    {
      title: "Маса",
      dataIndex: "tableNames",
      align: "center",
      key: "tableNames",
      width: "10%",
      render: (text, record) => (
        <div>
          {record.tableNames.map((table, index) => (
            <span key={index}>
              {table}
              {index < record.tableNames.length - 1 && ", "}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: "Бр. хора",
      dataIndex: "guestsCount",
      align: "center",
      key: "guestsCount",
      width: "10%",
      sorter: (a, b) => a.guestsCount - b.guestsCount,
      render: (text, record) => <div>{record.guestsCount}</div>,
    },
    {
      title: () => {
        const totalSum = filteredData.reduce(
          (sum, record) => sum + (record.touchsaleSum || 0),
          0
        );
        return (
          <div>
            <div>Сума</div>
            <div className="text-xs font-normal">
              (Общо: {totalSum.toFixed(2)})
            </div>
          </div>
        );
      },
      dataIndex: "touchsaleSum",
      align: "center",
      key: "touchsaleSum",
      width: "10%",
      sorter: (a, b) => (a.touchsaleSum || 0) - (b.touchsaleSum || 0),
      render: (text, record) => (
        <div>{record.touchsaleSum ? record.touchsaleSum.toFixed(2) : "-"}</div>
      ),
    },
    {
      title: "Име",
      dataIndex: "reservationName",
      align: "center",
      key: "reservationName",
      width: "10%",
      filterSearch: true,
      filters: [
        ...Array.from(
          new Set(reservationHistory.map((item) => item.reservationName))
        ).map((name) => ({ text: name, value: name })),
      ],
      onFilter: (value, record) => record.reservationName === value,
      sorter: (a, b) => a.reservationName.localeCompare(b.reservationName),
      render: (text, record) => <div>{record.reservationName}</div>,
    },
    {
      title: "Телефон",
      dataIndex: "phoneNumber",
      align: "center",
      key: "phoneNumber",
      width: "10%",
      filterSearch: true,
      filters: [
        ...Array.from(
          new Set(reservationHistory.map((item) => item.phoneNumber))
        ).map((phone) => ({ text: phone, value: phone })),
      ],
      onFilter: (value, record) => record.phoneNumber === value,
      sorter: (a, b) => a.phoneNumber.localeCompare(b.phoneNumber),
      render: (text, record) => <div>{record.phoneNumber}</div>,
    },
    {
      title: "Забележка",
      dataIndex: "note",
      align: "center",
      key: "note",
      width: "10%",
      render: (text, record) => <div>{record.note}</div>,
    },
    {
      title: () => {
        const totalDeposit = filteredData.reduce(
          (sum, record) => sum + (record.deposit || 0),
          0
        );
        return (
          <div>
            <div>Депозит</div>
            <div className="text-xs font-normal">
              (Общо: {totalDeposit.toFixed(2)})
            </div>
          </div>
        );
      },
      dataIndex: "deposit",
      align: "center",
      key: "deposit",
      width: "8%",
      sorter: (a, b) => (a.deposit || 0) - (b.deposit || 0),
      render: (text, record) => (
        <div style={{ color: record.deposit > 0 ? "#d32f2f" : undefined, fontWeight: record.deposit > 0 ? 600 : undefined }}>
          {record.deposit ? record.deposit.toFixed(2) : "-"}
        </div>
      ),
    },
  ];

  return (
    <>
      <ReferenceReservationStatistics reservationHistory={reservationHistory} />
      <Table
        titleStyle={{ bordered: false }}
        title={() => (
          <>
            <div className="reservation-reference-table-title">
              <InfoCircleOutlined />{" "}
              <span>Оцветените редове в червено са изтрити резервации.</span>
            </div>
            <div className="reservation-reference-table-title">
              <InfoCircleOutlined />{" "}
              <span>
                Оцветените редове в зелено са резервациите за текущия ден.
              </span>
            </div>
          </>
        )}
        size="small"
        rowKey={(record) =>
          `${record.reservationName}-${record.phoneNumber}-${
            record.guestsCount
          }-${uuidv4()}`
        }
        loading={loading}
        className="reservation-history-info-table"
        columns={columns}
        dataSource={reservationHistory}
        pagination={false}
        onChange={(pagination, filters, sorter, extra) => {
          setFilteredData(extra.currentDataSource);
        }}
        rowClassName={(record) => {
          // Check if the reservation is for the current day
          const isToday = dayjs(record.date).isSame(
            dayjs().startOf("day"),
            "day"
          );
          // Check if the reservation is marked as deleted
          const isDeleted = record.isDelete;

          // Deleted should always win over today styling
          if (isDeleted) {
            return "reference-reservations-table-deleted-reservations-background";
          }
          if (isToday) {
            return "reference-reservations-table-today-background";
          }
          return ""; // Default: no specific class
        }}
      />
    </>
  );
};

export default ReservationHistoryTable;
